/**
 * Phase-4 building exterior scene.
 *
 * Pure Pixi -- no React, no Zustand imports here.  The scene reads
 * the latest state snapshot from ``ctx.getState()`` on every redraw,
 * so React tells us *when* to refresh and the manager does the rest.
 *
 * Visual model is intentionally minimal for now: a building rectangle
 * with one row per floor; inside the row, a column of "windows", one
 * per active pod, coloured by ``risk_state``.  A window flickers when
 * ``flickerUntilMs > now`` (set by the React layer in response to
 * ``agent.signal_emitted`` events).  Click on a window selects that
 * pod via the on-click callback the manager wires up at construction.
 *
 * Pixel-art assets land in Phase 5 -- this scene currently draws
 * everything with ``Graphics`` so the bundle stays tiny and we keep
 * the spatial geometry stable while artwork iterates.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Scene, SceneContext, ScenePod } from './SceneManager';
import { intensityToMultiplier as intensityMultiplier } from './SceneManager';
import {
  hashSeed,
  layoutBuilding,
  mulberry32,
  signatureOf,
  type LaidOutPod,
} from './BuildingExteriorLayout';
import {
  clearBuildingPodHitRects,
  setBuildingPodHitRects,
  type BuildingPodHitRect,
} from './buildingHitRects';

// Re-export pure helpers for any consumer that historically imported
// them from this module (tests, debug tooling).
export {
  hashSeed,
  layoutBuilding,
  maxCharsFor,
  podFontFor,
  shortPodLabel,
} from './BuildingExteriorLayout';

const FLOOR_FILL: Record<string, number> = {
  canary_lab: 0x1f3a5f,
  active_offices: 0x21262d,
  reduced_floor: 0x3a2818,
  frozen_basement: 0x0e2740,
};

/** One-line floor purpose, rendered as an italic subtitle in the wide layout. */
const FLOOR_DESCRIPTION: Record<string, string> = {
  canary_lab: 'sandbox pods · shadow trades',
  active_offices: 'trading at full size',
  reduced_floor: 'haircut applied · soft breach',
  frozen_basement: 'suspended · hard breach',
};

const RISK_COLOR: Record<ScenePod['riskState'], number> = {
  active: 0x2ea043,
  watch: 0xd29922,
  reduced: 0xdb6d28,
  frozen: 0x58a6ff,
};

/**
 * Sky gradient stops by sun phase.  Hex pairs are ``[top, bottom]`` --
 * the band fades between them vertically to suggest dawn/dusk light.
 */
const SKY_GRADIENT: Record<string, [number, number]> = {
  predawn: [0x0d1733, 0x2a2554],
  morning: [0x2a4570, 0xf6b079],
  noon: [0x4a90c2, 0xa8d4e8],
  afternoon: [0x4a7fb3, 0xe8a45f],
  evening: [0x2a3b6e, 0xe87a4a],
  night: [0x0a1228, 0x1f2e54],
  weekend_moon: [0x0a1228, 0x1f2e54],
  holiday_moon: [0x0a1228, 0x1f2e54],
  default: [0x0d1117, 0x161b22],
};

/** Optional weather wash overlay: rgb hex + alpha. */
const WEATHER_OVERLAY: Record<string, { color: number; alpha: number }> = {
  sunny: { color: 0xffe7a3, alpha: 0 },
  cloudy: { color: 0x9aa0a6, alpha: 0.18 },
  storm: { color: 0x1f2937, alpha: 0.32 },
  windy: { color: 0xb8c1cc, alpha: 0.1 },
  snow: { color: 0xdfe7ff, alpha: 0.24 },
  fog: { color: 0x6e7681, alpha: 0.3 },
};

interface PodSprite {
  podId: string;
  container: Container;
  base: Graphics;
  glow: Graphics;
  /** Ice-crack overlay -- shown for ~2.4 s after risk.state_changed -> frozen.
   * Lives above ``base`` so the cracks scribble on top of the window. */
  ice: Graphics;
  label: Text;
  /** Optional daily-return text rendered under the pod label.  Hidden
   * (empty string) when the backend hasn't reported a ``daily_return``
   * yet. */
  pnlLabel: Text;
  /** NAV trajectory mini-sparkline drawn in the lower band of the
   *  window.  Hidden on short pod windows (mobile) where there isn't
   *  enough vertical room. */
  sparkline: Graphics;
  /** Dimensions captured at layout time so we don't read partially-
   * drawn container bounds from refreshExisting. */
  w: number;
  h: number;
  /** Random seed so each pod's crack pattern looks different but
   * stable between frames. */
  iceSeed: number;
}

export interface BuildingExteriorOptions {
  onSelectPod: (podId: string) => void;
}

export class BuildingExteriorScene implements Scene {
  private readonly options: BuildingExteriorOptions;
  private root: Container | null = null;
  private skyTop: Graphics | null = null;
  private skyBottom: Graphics | null = null;
  private celestialBody: Graphics | null = null;
  /** Atmospheric particles (snow / rain / fog) painted above the wash. */
  private weatherParticles: Graphics | null = null;
  private weatherWash: Graphics | null = null;
  /** Night-ritual dim layer painted during the ``building.daily_close``
   *  window.  Soft dark-blue wash + small "closing" tag in the corner. */
  private dailyCloseDim: Graphics | null = null;
  private dailyCloseLabel: Text | null = null;
  /** Camera ``building_overview`` / ``wide`` directive -- a soft
   *  golden ring around the whole building frame that fades over
   *  ~2.5 s.  ``focus_on_target`` keeps its existing spotlight
   *  behaviour (see ``refreshExisting``). */
  private cameraWideGlow: Graphics | null = null;
  /** Cached building frame bounds so ``cameraWideGlow`` knows where
   *  to draw without re-running the layout each frame. */
  private buildingBounds: { x: number; y: number; w: number; h: number } | null = null;
  private podSprites = new Map<string, PodSprite>();
  private signature = '';
  private atmosphereKey = '';

  constructor(options: BuildingExteriorOptions) {
    this.options = options;
  }

  enter(_ctx: SceneContext, container: Container): void {
    this.root = new Container();
    container.addChild(this.root);
  }

  exit(): void {
    this.podSprites.clear();
    this.skyTop = null;
    this.skyBottom = null;
    this.celestialBody = null;
    this.weatherParticles = null;
    this.weatherWash = null;
    this.dailyCloseDim = null;
    this.dailyCloseLabel = null;
    this.cameraWideGlow = null;
    this.buildingBounds = null;
    this.root?.destroy({ children: true });
    this.root = null;
    this.signature = '';
    this.atmosphereKey = '';
    // Drop the DOM tooltip overlay's hit-rect registry so a no-longer-
    // mounted scene doesn't ghost-hover after a route change.
    clearBuildingPodHitRects();
  }

  redraw(ctx: SceneContext): void {
    if (!this.root) return;
    const state = ctx.getState();
    // Signature folds in viewport size so a window resize / device
    // rotation forces a full relayout (e.g., crossing the narrow/wide
    // 600 px boundary swaps the floor strategy).
    const w = ctx.app.renderer.width;
    const h = ctx.app.renderer.height;
    const sig = `${w}x${h}|${signatureOf(state.buildingFloors)}`;
    if (sig === this.signature) {
      // Same shape -- just refresh colors/flicker + atmosphere, no rebuild.
      this.refreshAtmosphere(ctx);
      this.refreshExisting(ctx);
      return;
    }
    this.signature = sig;
    this.rebuild(ctx);
  }

  tick(ctx: SceneContext): void {
    // Animated bits (flicker, weather overlay) read state every frame;
    // bulk shape rebuild only happens on signature change.  We piggy-back
    // here to catch viewport resizes (Pixi auto-resizes the renderer
    // when its host element changes, but does not notify the Scene
    // layer) -- ``redraw`` re-checks the signature and rebuilds when
    // the cached width/height no longer match.
    if (!this.root) return;
    this.redraw(ctx);
    // Weather particles update every frame -- their positions depend
    // on ``performance.now`` so they keep drifting even when no events
    // arrive.
    this.refreshWeatherParticles(ctx);
  }

  private refreshWeatherParticles(ctx: SceneContext): void {
    if (!this.weatherParticles) return;
    const { app, getState } = ctx;
    const state = getState();
    const weather = state.weatherKind;
    const w = app.renderer.width;
    const h = app.renderer.height;
    this.weatherParticles.clear();
    if (!weather || weather === 'sunny') return;
    const t = performance.now();
    if (weather === 'snow') {
      drawSnowParticles(this.weatherParticles, w, h, t);
    } else if (weather === 'storm') {
      drawRainParticles(this.weatherParticles, w, h, t, 0.85);
    } else if (weather === 'windy') {
      drawRainParticles(this.weatherParticles, w, h, t, 0.4);
    } else if (weather === 'fog') {
      drawFogWisps(this.weatherParticles, w, h, t);
    } else if (weather === 'cloudy') {
      drawCloudDrift(this.weatherParticles, w, h, t);
    }
  }

  private rebuild(ctx: SceneContext): void {
    if (!this.root) return;
    this.root.removeChildren();
    this.podSprites.clear();
    this.atmosphereKey = '';

    const { app, getState } = ctx;
    const state = getState();
    const width = app.renderer.width;
    const height = app.renderer.height;

    // Sky band -- behind everything else.  Drawn as two stacked
    // rectangles (top + bottom half) whose colors are picked by
    // sun phase; ``refreshAtmosphere`` repaints them every redraw.
    this.skyTop = new Graphics();
    this.skyBottom = new Graphics();
    this.celestialBody = new Graphics();
    this.root.addChild(this.skyTop);
    this.root.addChild(this.skyBottom);
    this.root.addChild(this.celestialBody);

    const layout = layoutBuilding({ width, height, floors: state.buildingFloors });
    this.buildingBounds = { x: layout.x, y: layout.y, w: layout.w, h: layout.h };

    // Outer building frame.
    const frame = new Graphics()
      .roundRect(layout.x, layout.y, layout.w, layout.h, 18)
      .fill({ color: 0x0d1117, alpha: 0.92 })
      .stroke({ width: 2, color: 0x30363d, alignment: 1 });
    this.root.addChild(frame);

    for (const floor of layout.floors) {
      // Floor row background.
      const floorBg = new Graphics()
        .roundRect(floor.x, floor.y, floor.w, floor.h, 10)
        .fill({ color: FLOOR_FILL[floor.id] ?? 0x21262d });
      this.root.addChild(floorBg);

      // Floor label -- positioned per ``labelAnchor`` so the wide
      // layout keeps the "label-on-the-left" look while the narrow
      // layout pins the label to the top of the row above its pod grid.
      const labelStyle = new TextStyle({
        fill: 0x8b949e,
        fontFamily: 'system-ui, sans-serif',
        fontSize: floor.labelFontSize,
        fontWeight: '600',
      });
      const label = new Text({ text: floor.label, style: labelStyle });
      if (floor.labelAnchor === 'top-left') {
        label.position.set(floor.x + 10, floor.y + 4);
      } else {
        label.position.set(floor.x + 12, floor.y + 8);
      }
      this.root.addChild(label);

      // Tiny description subtitle under the floor name -- gives
      // first-time visitors a one-line answer to "what is this row
      // for?" without needing to open the help legend.  Skipped on
      // very narrow floors where the description would crowd the
      // pods.
      const descText = FLOOR_DESCRIPTION[floor.id];
      const descFontSize = Math.max(9, floor.labelFontSize * 0.55);
      const hasRoomForDesc =
        floor.labelAnchor === 'inset-left'
          ? floor.h > descFontSize * 4
          : false; // narrow layout has no spare vertical space below the label
      if (descText && hasRoomForDesc) {
        const descStyle = new TextStyle({
          fill: 0x6e7681,
          fontFamily: 'system-ui, sans-serif',
          fontSize: descFontSize,
          fontStyle: 'italic',
        });
        const desc = new Text({ text: descText, style: descStyle });
        desc.position.set(label.x, label.y + floor.labelFontSize + 4);
        this.root.addChild(desc);
      }

      // Render one "window" per pod.
      for (const pod of floor.pods) {
        const sprite = this.buildPodSprite(pod);
        this.podSprites.set(pod.podId, sprite);
        this.root.addChild(sprite.container);
      }
    }

    // Republish hit-rects for the DOM tooltip overlay -- one per pod,
    // in CSS-pixel coordinates (Pixi's ``autoDensity`` keeps Pixi's
    // pixel space aligned with the host's CSS-pixel space).  Look up
    // the full pod name from the snapshot (LaidOutPod only carries
    // ``shortName`` because the label is space-constrained).
    const fullNames: Record<string, string> = {};
    for (const floor of state.buildingFloors) {
      for (const pod of floor.pods) fullNames[pod.podId] = pod.name;
    }
    const rects: BuildingPodHitRect[] = [];
    for (const floor of layout.floors) {
      for (const pod of floor.pods) {
        rects.push({
          podId: pod.podId,
          name: fullNames[pod.podId] ?? pod.shortName,
          x: pod.x,
          y: pod.y,
          w: pod.w,
          h: pod.h,
        });
      }
    }
    setBuildingPodHitRects(rects);

    // Weather wash overlay sits above everything so storms/fog cool the
    // whole scene.  Drawn as a transparent rectangle whose color +
    // alpha are picked in ``refreshAtmosphere``.
    this.weatherWash = new Graphics();
    this.root.addChild(this.weatherWash);

    // Particle layer sits above the wash -- snowflakes / rain streaks /
    // fog wisps drift over everything.  Repainted every frame from
    // ``tick`` because the particle positions are time-dependent.
    this.weatherParticles = new Graphics();
    this.root.addChild(this.weatherParticles);

    // Camera "wide" / "building_overview" glow sits between particles
    // and the daily-close dim so the dim still wins visually when
    // both are active.
    this.cameraWideGlow = new Graphics();
    this.root.addChild(this.cameraWideGlow);

    // Daily-close dim sits above particles so the night ritual reads
    // unmistakably "lights out" even while it's snowing.  The label is
    // tucked in the top-left corner so it doesn't fight with the
    // header banner that says the same thing.
    this.dailyCloseDim = new Graphics();
    this.root.addChild(this.dailyCloseDim);
    const closeLabelStyle = new TextStyle({
      fill: 0xe6edf3,
      fontFamily: 'system-ui, sans-serif',
      fontSize: 11,
      fontWeight: '600',
    });
    this.dailyCloseLabel = new Text({ text: '', style: closeLabelStyle });
    this.dailyCloseLabel.alpha = 0;
    this.dailyCloseLabel.position.set(12, 12);
    this.root.addChild(this.dailyCloseLabel);

    this.refreshAtmosphere(ctx);
    this.refreshExisting(ctx);
  }

  private refreshAtmosphere(ctx: SceneContext): void {
    if (
      !this.root ||
      !this.skyTop ||
      !this.skyBottom ||
      !this.celestialBody ||
      !this.weatherWash
    ) {
      return;
    }
    const { app, getState } = ctx;
    const state = getState();
    const w = app.renderer.width;
    const h = app.renderer.height;

    const phase = state.sunPhase ?? 'default';
    const weather = state.weatherKind ?? 'sunny';
    const elev = state.sunElevationDeg;
    const azim = state.sunAzimuthDeg;
    const closePhase = state.dailyClose?.phase ?? '–';
    const key = `${w}x${h}|${phase}|${weather}|${elev ?? '–'}|${azim ?? '–'}|${closePhase}`;
    if (key === this.atmosphereKey) return;
    this.atmosphereKey = key;

    const [topColor, bottomColor] =
      SKY_GRADIENT[phase] ?? SKY_GRADIENT.default ?? [0x0d1117, 0x161b22];
    this.skyTop.clear().rect(0, 0, w, h / 2).fill({ color: topColor });
    this.skyBottom.clear().rect(0, h / 2, w, h / 2).fill({ color: bottomColor });

    // Sun/moon disc.  Position derived from elevation (vertical) and
    // azimuth (horizontal); a clamped 10% top margin keeps the body
    // visible even at noon (90deg elevation -> very top).
    this.celestialBody.clear();
    if (elev != null && azim != null) {
      const isNight = elev < 0;
      const radius = Math.max(14, Math.min(w, h) * 0.04);
      // Normalised position: az 90deg (east) -> x = 10%, az 270deg (west)
      // -> x = 90%.  Linear and intentionally cartoonish.
      const xFrac = Math.min(0.92, Math.max(0.08, ((azim - 60) / 240) * 0.84 + 0.08));
      // Elevation 0deg -> bottom 20% of viewport; 90deg -> top 10%.
      const elevNorm = Math.min(1, Math.max(-1, elev / 90));
      const yFrac =
        elevNorm >= 0 ? 0.8 - elevNorm * 0.7 : 0.85 + Math.abs(elevNorm) * 0.1;
      const cx = w * xFrac;
      const cy = h * yFrac;
      const fillColor = isNight ? 0xe6e6f0 : 0xfff1a8;
      const glowColor = isNight ? 0x9aa3c2 : 0xffd966;
      // Soft glow ring under the disc.
      this.celestialBody
        .circle(cx, cy, radius * 1.8)
        .fill({ color: glowColor, alpha: 0.18 })
        .circle(cx, cy, radius * 1.3)
        .fill({ color: glowColor, alpha: 0.28 })
        .circle(cx, cy, radius)
        .fill({ color: fillColor, alpha: isNight ? 0.92 : 1 });
      if (isNight) {
        // Crescent: subtract a smaller disc offset to the side.
        this.celestialBody
          .circle(cx - radius * 0.35, cy - radius * 0.15, radius * 0.85)
          .fill({ color: SKY_GRADIENT[phase]?.[0] ?? 0x0a1228, alpha: 0.92 });
      }
    }

    const wash = WEATHER_OVERLAY[weather] ?? { color: 0, alpha: 0 };
    this.weatherWash
      .clear()
      .rect(0, 0, w, h)
      .fill({ color: wash.color, alpha: wash.alpha });

    // Daily-close night ritual: while ``phase === started`` paint a
    // soft dark-blue dim over the whole canvas + show a small
    // "Daily close" label.  ``completed`` lifts the dim immediately.
    // The header banner from ``<SessionBanner>`` already announces the
    // phase text -- the Pixi tag is a secondary cue for the lights
    // dimming visually.
    if (this.dailyCloseDim) {
      this.dailyCloseDim.clear();
      if (state.dailyClose?.phase === 'started') {
        this.dailyCloseDim
          .rect(0, 0, w, h)
          .fill({ color: 0x0a1228, alpha: 0.42 });
      }
    }
    if (this.dailyCloseLabel) {
      if (state.dailyClose?.phase === 'started') {
        this.dailyCloseLabel.text = '✦ Daily close in progress';
        this.dailyCloseLabel.alpha = 0.85;
      } else {
        this.dailyCloseLabel.text = '';
        this.dailyCloseLabel.alpha = 0;
      }
    }
  }

  private buildPodSprite(pod: LaidOutPod): PodSprite {
    const container = new Container();
    container.position.set(pod.x, pod.y);
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', () => this.options.onSelectPod(pod.podId));

    const glow = new Graphics();
    container.addChild(glow);

    const base = new Graphics();
    container.addChild(base);

    // Ice-crack overlay sits above the base so the cracks score the
    // window; stays invisible (cleared) until the React layer flips
    // ``iceUntilMs`` for this pod.
    const ice = new Graphics();
    container.addChild(ice);

    const labelStyle = new TextStyle({
      fill: 0xe6edf3,
      fontFamily: 'system-ui, sans-serif',
      fontSize: pod.fontSize,
      fontWeight: '600',
    });
    const label = new Text({ text: pod.shortName, style: labelStyle });
    label.anchor.set(0.5, 0.5);
    // Move the label up so the daily-pnl line has room below it on
    // taller pod windows.  On short windows (mobile) the pnl line is
    // hidden, so the label re-centers in ``refreshExisting``.
    const hasRoomForPnl = pod.h >= 56;
    label.position.set(pod.w / 2, hasRoomForPnl ? pod.h / 2 - pod.fontSize * 0.55 : pod.h / 2);
    container.addChild(label);

    // Secondary line: daily return.  Filled in every frame from the
    // pod snapshot in ``refreshExisting``.
    const pnlStyle = new TextStyle({
      fill: 0x8b949e,
      fontFamily: 'system-ui, sans-serif',
      fontSize: Math.max(9, pod.fontSize - 2),
      fontWeight: '600',
    });
    const pnlLabel = new Text({ text: '', style: pnlStyle });
    pnlLabel.anchor.set(0.5, 0.5);
    pnlLabel.position.set(pod.w / 2, pod.h / 2 + pod.fontSize * 0.7);
    pnlLabel.visible = hasRoomForPnl;
    container.addChild(pnlLabel);

    // NAV trajectory sparkline -- thin line near the bottom edge of
    // the window.  Re-drawn every frame from ``navHistory`` in
    // ``refreshExisting``; only visible on pods tall enough to host
    // the pnl line *and* a strip of sparkline below it.
    const sparkline = new Graphics();
    container.addChild(sparkline);

    // Stable per-pod seed so the ice-crack pattern doesn't jitter
    // between frames -- hashed from the podId.
    const iceSeed = hashSeed(pod.podId);
    return {
      podId: pod.podId,
      container,
      base,
      glow,
      ice,
      label,
      pnlLabel,
      sparkline,
      w: pod.w,
      h: pod.h,
      iceSeed,
    };
  }

  private refreshExisting(ctx: SceneContext): void {
    const now = performance.now();
    const state = ctx.getState();
    // ``scene_hint.camera === 'focus_on_target'`` paints a 2.5 s
    // spotlight: the targeted pod gets a yellow halo + full
    // opacity, every other pod fades to 0.35 alpha.  Approximates
    // the "zoom/pan to target" intent without actually transforming
    // the Pixi camera (which would require wrapping the entire
    // scene in a world container).
    const SPOTLIGHT_MS = 2_500;
    const cf = state.cameraFocus;
    const spotlightActive =
      cf != null &&
      cf.kind === 'focus_on_target' &&
      cf.podId != null &&
      now - cf.setAtMs < SPOTLIGHT_MS;
    const spotlightPodId = spotlightActive ? cf?.podId ?? null : null;

    // ``wide`` / ``building_overview`` / ``party_hall`` paint a
    // brief golden ring around the whole building instead of
    // dimming pods.  Decays linearly over SPOTLIGHT_MS so the cue
    // doesn't linger forever.
    const WIDE_GLOW_KINDS = new Set([
      'wide',
      'building_overview',
      'floor_overview',
      'party_hall',
    ]);
    if (this.cameraWideGlow) {
      this.cameraWideGlow.clear();
      if (
        cf != null &&
        WIDE_GLOW_KINDS.has(cf.kind) &&
        this.buildingBounds != null
      ) {
        const ageMs = now - cf.setAtMs;
        if (ageMs < SPOTLIGHT_MS) {
          const fade = 1 - ageMs / SPOTLIGHT_MS;
          // Soft outer ring -- two concentric strokes that fade together
          // so the building reads as "the camera just panned out".
          const b = this.buildingBounds;
          const ring = cf.kind === 'party_hall' ? 0xffd33d : 0xf2cc60;
          this.cameraWideGlow
            .roundRect(b.x - 8, b.y - 8, b.w + 16, b.h + 16, 22)
            .stroke({ width: 4, color: ring, alpha: 0.32 * fade });
          this.cameraWideGlow
            .roundRect(b.x - 14, b.y - 14, b.w + 28, b.h + 28, 26)
            .stroke({ width: 6, color: ring, alpha: 0.16 * fade });
        }
      }
    }
    for (const floor of state.buildingFloors) {
      for (const pod of floor.pods) {
        const sprite = this.podSprites.get(pod.podId);
        if (!sprite) continue;
        const accent = RISK_COLOR[pod.riskState];
        const flickering = pod.flickerUntilMs > now;
        const baseFill = pod.isCanary ? 0x1f6feb : 0x21262d;
        const { w, h } = sprite;
        // Spotlight: bring the targeted pod to full brightness, dim
        // everything else.  Skipped when no recent focus directive.
        // Dim is aggressive (0.18) so the spotlight reads even on a
        // narrow building view where each pod is only ~50 px wide.
        const dimmed =
          spotlightActive && spotlightPodId !== pod.podId ? 0.18 : 1.0;
        sprite.container.alpha = dimmed;
        const isTarget = spotlightActive && spotlightPodId === pod.podId;

        sprite.base
          .clear()
          .roundRect(0, 0, w, h, 6)
          .fill({ color: baseFill, alpha: pod.riskState === 'frozen' ? 0.55 : 0.9 })
          .stroke({
            width: isTarget
              ? 3.2
              : pod.isCanary || pod.cycleInFlight
                ? 2.4
                : 1.4,
            color: isTarget ? 0xffd33d : accent,
          });

        // Cycle-in-flight border highlight: a soft glow around the
        // pod window that pulses while the cycle is running, so the
        // building view shows at a glance which pods are actively
        // computing.  Suppresses the regular flicker so the two
        // don't fight visually.
        //
        // ``lastIntensity`` (low/medium/high/critical) scales both the
        // pulse speed and the brightness so a critical event is
        // visible from the building exterior, not only inside the
        // pod room.
        const intMul = intensityMultiplier(pod.lastIntensity);
        const pulseBase = 0.22 + Math.min(0.25, 0.05 * (intMul - 1));
        const pulseAmp = 0.18 * intMul;
        const pulseSpeed = 200 / intMul; // smaller divisor = faster
        sprite.glow
          .clear()
          .roundRect(-3, -3, w + 6, h + 6, 10)
          .fill({
            color: accent,
            alpha: flickering
              ? 0.55
              : pod.cycleInFlight
                ? pulseBase + pulseAmp * Math.sin(now / pulseSpeed)
                : 0,
          });

        // Daily P&L line: shows pod performance at a glance without
        // having to step into the pod room.  Hidden on short pod
        // windows (mobile) because there isn't enough vertical room.
        if (sprite.pnlLabel.visible) {
          const dr = pod.dailyReturn;
          const dp = pod.dailyPnl;
          if (dr != null) {
            const pct = dr * 100;
            sprite.pnlLabel.text = `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
            sprite.pnlLabel.style.fill =
              pct >= 0 ? 0x3fb950 : 0xf85149;
          } else if (dp != null) {
            sprite.pnlLabel.text =
              `${dp >= 0 ? '+' : ''}${Math.round(dp / 1000)}k`;
            sprite.pnlLabel.style.fill =
              dp >= 0 ? 0x3fb950 : 0xf85149;
          } else {
            sprite.pnlLabel.text = '';
          }
        }

        // NAV sparkline: a tiny intraday trajectory rendered along the
        // bottom edge of the window.  Need at least 2 points and ~14 px
        // of vertical headroom (so the line doesn't collide with the
        // pnl text or the rounded corners).
        drawNavSparkline(sprite.sparkline, sprite.w, sprite.h, pod);

        // Ice-crack overlay: visible while the React layer's deadline
        // is in the future.  Cracks fade out over the last 600 ms of
        // the window so the freeze event feels punchy at the start
        // and gracefully recedes.
        sprite.ice.clear();
        const remaining = pod.iceUntilMs - now;
        if (remaining > 0) {
          const fadeOutMs = 600;
          const alpha =
            remaining < fadeOutMs ? Math.max(0, remaining / fadeOutMs) : 1;
          drawIceCracks(sprite.ice, w, h, sprite.iceSeed, alpha);
          continue; // skip the activity dot while the pod is iced
        }

        // "Alive" activity dot in the top-right corner.  Visible if
        // the pod has had any event in the last ACTIVITY_WINDOW_MS;
        // the dot brightness fades to zero as the freshness decays.
        const activityAge = now - pod.lastActivityAt;
        if (pod.lastActivityAt > 0 && activityAge < ACTIVITY_WINDOW_MS) {
          const freshness = 1 - activityAge / ACTIVITY_WINDOW_MS;
          // 2 Hz pulse, scaled by freshness so older activity dims out.
          const pulse = 0.55 + 0.45 * Math.sin(now / 200);
          const dotRadius = 3 + freshness * 1.5;
          const ax = w - 7;
          const ay = 7;
          // Halo behind the dot.
          sprite.ice
            .circle(ax, ay, dotRadius + 4)
            .fill({ color: 0x2ea043, alpha: freshness * 0.18 * pulse });
          sprite.ice
            .circle(ax, ay, dotRadius)
            .fill({ color: 0x2ea043, alpha: 0.5 + freshness * 0.5 });
        }

        // Top-left badges -- discipline severity dot + canary "C" +
        // centerbook scale.  All small, layered left-to-right so they
        // never overlap each other.
        let badgeX = 6;
        const badgeY = 6;
        // Discipline severity dot -- yellow/orange/red/crimson by tier.
        const sevColor =
          pod.disciplineSeverity === 'critical'
            ? 0xff3030
            : pod.disciplineSeverity === 'high'
              ? 0xf85149
              : pod.disciplineSeverity === 'medium'
                ? 0xd29922
                : pod.disciplineSeverity === 'low'
                  ? 0xffd33d
                  : null;
        if (sevColor != null) {
          // 1.6 Hz pulse for critical, gentle dot otherwise.
          const sevAlpha =
            pod.disciplineSeverity === 'critical'
              ? 0.6 + 0.4 * Math.sin(now / 250)
              : 0.95;
          sprite.ice
            .circle(badgeX + 4, badgeY + 4, 5)
            .fill({ color: sevColor, alpha: sevAlpha });
          badgeX += 12;
        }
        // Canary badge -- small bright blue dot for sandbox pods.
        if (pod.isCanary) {
          sprite.ice
            .circle(badgeX + 4, badgeY + 4, 4)
            .fill({ color: 0x1f6feb, alpha: 0.95 });
          badgeX += 11;
        }
        // "Moving" badge -- pod is mid-floor-transition (``room.exited``
        // landed without a matching ``room.entered`` yet).  Drawn as a
        // small upward-pointing chevron that pulses, so the staircase
        // shuffle is visible from the exterior.
        if (pod.isMoving) {
          const pulse = 0.55 + 0.4 * Math.abs(Math.sin(now / 220));
          const cx = badgeX + 5;
          const cy = badgeY + 5;
          sprite.ice
            .moveTo(cx - 4, cy + 2)
            .lineTo(cx, cy - 3)
            .lineTo(cx + 4, cy + 2)
            .stroke({ width: 1.4, color: 0xffd33d, alpha: pulse });
          // Second smaller chevron below for "stairs" feel.
          sprite.ice
            .moveTo(cx - 3, cy + 5)
            .lineTo(cx, cy + 1)
            .lineTo(cx + 3, cy + 5)
            .stroke({ width: 1.1, color: 0xffd33d, alpha: pulse * 0.6 });
          badgeX += 12;
        }
      }
    }
  }
}

const ACTIVITY_WINDOW_MS = 30_000;

/**
 * Render the pod's NAV trajectory as a tiny sparkline on the lower
 * edge of the window.  Tinted by latest intraday return: green when
 * up, red when down, grey when flat / unknown.  Anchored to the
 * window's local coords (``container.position`` is the pod's top-left,
 * so we draw in pod-window space).
 *
 * Hidden when:
 *  * the window is too short (need ~14 px below the pnl line);
 *  * fewer than 2 nav points are available (a single dot isn't a
 *    trajectory, it's just clutter).
 */
function drawNavSparkline(g: Graphics, w: number, h: number, pod: ScenePod): void {
  g.clear();
  const hist = pod.navHistory;
  if (!hist || hist.length < 2) return;
  // Reserve a 12 px strip near the bottom edge.  ``hasRoomForSpark``
  // mirrors the ``hasRoomForPnl`` gate in ``buildPodSprite``: only
  // pods with room for a daily-pnl line also get a sparkline below.
  const stripH = 10;
  const padBottom = 6;
  const padX = 7;
  if (h < 72) return;
  const innerW = Math.max(0, w - padX * 2);
  if (innerW < 24) return;
  const topY = h - padBottom - stripH;
  const baseY = h - padBottom;

  // Up to ~12 of the most recent points -- enough resolution to show
  // a trend at this size, more than that turns into noise.  Trim from
  // the older end so the rightmost point is always "now".
  const points = hist.slice(-12);

  // Min/max NAV so the line uses the full vertical strip regardless of
  // absolute scale.  When all values are equal (typical right after
  // open), draw a single midline so the strip doesn't go blank.
  let minNav = Infinity;
  let maxNav = -Infinity;
  for (const p of points) {
    if (p.nav < minNav) minNav = p.nav;
    if (p.nav > maxNav) maxNav = p.nav;
  }
  if (!Number.isFinite(minNav) || !Number.isFinite(maxNav)) return;
  const navRange = Math.max(0.000001, maxNav - minNav);
  const flat = maxNav - minNav < 1e-9;

  // Tint by the most recent intraday-return sign; fall back to last-vs-
  // first NAV when the backend hasn't computed a return yet (e.g. very
  // early in the session).
  const latest = points[points.length - 1]!;
  const earliest = points[0]!;
  const trend = latest.intradayReturn ?? (latest.nav - earliest.nav) / (earliest.nav || 1);
  const colour = trend > 0.0005 ? 0x3fb950 : trend < -0.0005 ? 0xf85149 : 0x8b949e;

  // Subtle baseline so the eye registers the strip even when flat.
  g.moveTo(padX, baseY).lineTo(padX + innerW, baseY).stroke({ width: 1, color: 0x30363d, alpha: 0.55 });

  if (flat) {
    const midY = (topY + baseY) / 2;
    g.moveTo(padX, midY).lineTo(padX + innerW, midY).stroke({ width: 1.4, color: colour, alpha: 0.85 });
    return;
  }

  const step = innerW / (points.length - 1);
  // Build the line.  Top of strip = max NAV, bottom = min NAV.
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]!;
    const x = padX + i * step;
    const yNorm = (p.nav - minNav) / navRange; // 0..1, higher NAV -> closer to top
    const y = baseY - yNorm * stripH;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.stroke({ width: 1.4, color: colour, alpha: 0.95 });

  // Small dot on the most recent point so it pops; helps the eye lock
  // onto "current" amongst the trail.
  const lastX = padX + (points.length - 1) * step;
  const lastY = baseY - ((latest.nav - minNav) / navRange) * stripH;
  g.circle(lastX, lastY, 1.6).fill({ color: colour, alpha: 1 });
}

/**
 * Scribble 3 jagged ice cracks across a pod-window box.  Plus a
 * frosty rim so the freeze read is unmistakable.
 */
function drawIceCracks(
  g: Graphics,
  w: number,
  h: number,
  seed: number,
  alpha: number,
): void {
  const rand = mulberry32(seed);
  // Cold-blue wash that tints the whole pod-window interior.  Brighter
  // than the regular ``frozen`` risk color so the freeze EVENT reads
  // even on already-frozen pods.
  g.roundRect(0, 0, w, h, 6)
    .fill({ color: 0xa3c8ff, alpha: alpha * 0.22 });

  // Frosty rim -- a translucent light-blue inner border, thick enough
  // to register at typical screenshot scales.
  g.roundRect(1.5, 1.5, w - 3, h - 3, 5)
    .stroke({ width: 2.5, color: 0xc8e1ff, alpha: alpha * 0.9 });

  // 4 cracks: each starts on a random edge and zigzags across.
  const crackCount = 4;
  for (let c = 0; c < crackCount; c++) {
    const startSide = Math.floor(rand() * 4); // 0=top 1=right 2=bottom 3=left
    let x: number;
    let y: number;
    if (startSide === 0) {
      x = rand() * w;
      y = 0;
    } else if (startSide === 1) {
      x = w;
      y = rand() * h;
    } else if (startSide === 2) {
      x = rand() * w;
      y = h;
    } else {
      x = 0;
      y = rand() * h;
    }
    g.moveTo(x, y);
    const segments = 4 + Math.floor(rand() * 3);
    for (let s = 0; s < segments; s++) {
      x += (rand() - 0.5) * w * 0.55;
      y += (rand() - 0.5) * h * 0.55;
      x = Math.max(2, Math.min(w - 2, x));
      y = Math.max(2, Math.min(h - 2, y));
      g.lineTo(x, y);
    }
    g.stroke({ width: 2.2, color: 0xffffff, alpha: alpha * 0.95 });
  }
}

/**
 * Drifting snowflakes -- 50 deterministic columns, each falling with
 * its own speed and a slight horizontal sway.  Positions are derived
 * directly from ``time`` so the scene stays stateless.
 */
function drawSnowParticles(
  g: Graphics,
  w: number,
  h: number,
  time: number,
): void {
  const flakes = 50;
  for (let i = 0; i < flakes; i++) {
    // Stable per-flake seed.
    const seed = (i * 9301 + 49297) % 233280;
    const xBase = ((seed / 233280) * w) % w;
    const speed = 22 + (seed % 60); // px/sec
    const sway = 18 + ((seed * 7) % 22);
    const swayHz = 0.4 + (seed % 7) * 0.07;
    const phase = (seed % 100) / 100;
    const y = ((time * speed) / 1000 + seed * 13) % (h + 24) - 12;
    const x = xBase + Math.sin(time / 1000 * swayHz + phase * Math.PI * 2) * sway;
    const size = 1.6 + (seed % 5) * 0.5;
    g.circle(x, y, size).fill({ color: 0xffffff, alpha: 0.78 });
  }
}

/**
 * Diagonal rain streaks -- short straight lines slanted left-to-right.
 * ``intensity`` 0..1 controls streak count + opacity.
 */
function drawRainParticles(
  g: Graphics,
  w: number,
  h: number,
  time: number,
  intensity: number,
): void {
  const streaks = Math.round(40 + intensity * 80);
  const angle = -0.35; // slant
  const len = 14 + intensity * 8;
  const speed = 600 + intensity * 400; // px/sec
  for (let i = 0; i < streaks; i++) {
    const seed = (i * 1103515245 + 12345) >>> 0;
    const xBase = (seed % w);
    const yBase = ((seed >> 8) % h);
    const drift = ((time * speed) / 1000 + seed * 0.3) % (h + len);
    const x = xBase + drift * angle;
    const y = (yBase + drift) % (h + len) - len;
    const x2 = x + len * angle;
    const y2 = y + len;
    g.moveTo(x, y)
      .lineTo(x2, y2)
      .stroke({ width: 1.2, color: 0xa3c4ff, alpha: 0.45 + intensity * 0.25 });
  }
}

/**
 * Soft horizontal fog wisps -- 6 wide translucent bands drifting across
 * the canvas.  Lower opacity than rain/snow so the scene stays legible.
 */
function drawFogWisps(g: Graphics, w: number, h: number, time: number): void {
  const wisps = 6;
  for (let i = 0; i < wisps; i++) {
    const seed = i * 71;
    const yFrac = 0.12 + (i / wisps) * 0.78;
    const y = h * yFrac;
    const speed = 30 + (seed % 30);
    const x = ((time * speed) / 1000 + seed * 100) % (w + 320) - 320;
    const bandW = 280 + (seed % 90);
    const bandH = 24 + (seed % 14);
    g.roundRect(x, y - bandH / 2, bandW, bandH, bandH / 2)
      .fill({ color: 0xb8c5d6, alpha: 0.12 });
  }
}

/**
 * A handful of slow-drifting clouds -- minimal motion so it's clearly
 * "cloudy" without being distracting.
 */
function drawCloudDrift(g: Graphics, w: number, h: number, time: number): void {
  const clouds = 5;
  for (let i = 0; i < clouds; i++) {
    const seed = i * 137;
    const yFrac = 0.08 + (i / clouds) * 0.4;
    const y = h * yFrac;
    const speed = 12 + (seed % 14);
    const x = ((time * speed) / 1000 + seed * 90) % (w + 220) - 220;
    const r = 22 + (seed % 14);
    g.circle(x, y, r).fill({ color: 0xe6edf3, alpha: 0.14 });
    g.circle(x + r * 0.9, y + r * 0.15, r * 0.85).fill({ color: 0xe6edf3, alpha: 0.14 });
    g.circle(x + r * 1.7, y - r * 0.05, r * 0.7).fill({ color: 0xe6edf3, alpha: 0.14 });
  }
}
